import React, { forwardRef, useState } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { Box, Button, CircularProgress, Input } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiCreatePortfolio, apiGetPortfolio, apiUploadProfile } from '@store';
import { showToast } from '@utils';
import { useDispatch, useSelector } from 'react-redux';
import { useCountries } from '../../../hooks/useCountry';

const allowedCountries = [
    { name: 'United Kingdom', code: '+44' },
    { name: 'Switzerland', code: '+41' },
    { name: 'Germany', code: '+49' },
    { name: 'Poland', code: '+48' },
    { name: 'France', code: '+33' },
    { name: 'Belgium', code: '+32' },
    { name: 'Hungary', code: '+36' }
];

const CreatePortfolio = forwardRef(({ Users, handleClose }, ref) => {
    const { user } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const { account, isGetAccountLoading } = useSelector(
        (state) => state.account
    );
    const { countries, loading, error } = useCountries();

    const initialState = {
        name: '',
        currency: '',
        country: '',
        location: '',
        phone: '',
        country_code: '',
        commodity: '',
        account: '',
        account_name: '',
        account_uuid: '',
        Endpoint: ''
    };

    const [state, setState] = useState(initialState);

    const handleChange = (event) => {
        const key = event.target.name || event.target.id;
        const value = event.target.value;
        if (key === 'country') {
            const allowed = allowedCountries.find((c) => c.name === value);
            setState((prev) => ({
                ...prev,
                country: value,
                country_code: allowed?.code || ''
            }));
            return;
        }
        setState((prev) => ({
            ...prev,
            [key]: key === 'phone' ? value.replace(/\D/g, '') : value
        }));
    };

    const handleEndpointChange = (event) => {
        const selected = event.target.value;

        setState((prev) => ({
            ...prev,
            Endpoint: selected || ''
        }));
    };

    const handleAccountChange = (event) => {
        const selectedUuid = event.target.value;
        const selectedAccount = account?.accounts?.find(
            (acc) => acc.uuid === selectedUuid
        );

        setState((prev) => ({
            ...prev,
            account: selectedUuid,
            account_uuid: selectedUuid,
            account_name: selectedAccount?.company_name || ''
        }));
    };

    const [profileState, setProfileState] = useState({
        portfolio_name: '',
        data_type: 'demand1',
        myFile: null,
        portfolio_uuid: ''
    });

    const handleChangeFile = (event) => {
        const {
            target: { files }
        } = event;
        setProfileState({
            ...profileState,
            myFile: files[0]
        });
    };

    const handleUploadProfile = async (portfolio_uuid, portfolio_name) => {
        const formData = new FormData();
        formData.append('portfolio_name', portfolio_name);
        formData.append('data_type', profileState.data_type);
        formData.append('myFile', profileState.myFile);
        formData.append('portfolio_uuid', portfolio_uuid);

        const resp = await dispatch(apiUploadProfile(formData));
        if (resp?.meta?.requestStatus === 'fulfilled') {
            dispatch(apiGetPortfolio());
        } else if (resp?.meta?.requestStatus === 'rejected') {
            showToast('error', resp.payload);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { account: _account, Endpoint, country_code, ...rest } = state;
        const data = {
            ...rest,
            Endpoint: Endpoint || '',
            phone: country_code + state.phone
        };

        const resp = await dispatch(apiCreatePortfolio(data));
        if (resp?.meta?.requestStatus === 'fulfilled') {
            const portfolio_uuid = resp.payload.data.uuid;
            const portfolio_name = resp.payload.data.name;
            if (profileState.myFile) {
                handleUploadProfile(portfolio_uuid, portfolio_name);
            }
            dispatch(apiGetPortfolio());
            handleClose();
            setProfileState({
                portfolio_name: '',
                data_type: 'demand1',
                myFile: null,
                portfolio_uuid: ''
            });
            showToast('success', 'Portfolio created successfully');
        } else if (resp?.meta?.requestStatus === 'rejected') {
            showToast('error', resp.payload);
        }
    };

    return (
        <Box
            ref={ref}
            className="modal-container"
            component="form"
            onSubmit={handleSubmit}
        >
            <Box className="flex justify-between modal-header">
                <h2 className="modal-title">Create Portfolio</h2>
                <CloseIcon className="cursor-pointer" onClick={handleClose} />
            </Box>
            <div className="modal-body">
                <Box className="flex gap-3">
                    <FormControl
                        fullWidth={true}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        <InputLabel id="nameLabel">Name *</InputLabel>
                        <OutlinedInput
                            id="name"
                            label="Name *"
                            name="name"
                            value={state.name}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>
                    <FormControl
                        fullWidth={true}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        error={!!error}
                    >
                        <InputLabel id="countryLabel">Country *</InputLabel>
                        <Select
                            labelId="countryLabel"
                            id="country"
                            name="country"
                            value={state.country || ''}
                            onChange={handleChange}
                            label="Country *"
                            required
                            disabled={loading}
                            endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 3 }} /> : null}
                        >
                            {error ? (
                                <MenuItem disabled value="">
                                    <em>Error loading countries</em>
                                </MenuItem>
                            ) : (
                                countries
                                    .filter((countryName) => allowedCountries.some((c) => c.name === countryName))
                                    .map((countryName) => (
                                        <MenuItem key={countryName} value={countryName}>
                                            {countryName}
                                        </MenuItem>
                                    ))
                            )}
                        </Select>
                    </FormControl>
                </Box>
                <Box className="flex gap-3">
                    <Box sx={{ display: 'flex', gap: '10px', flex: 1, minWidth: 0, mb: 2 }}>
                        <FormControl sx={{ width: '90px' }}>
                            <InputLabel id="countryCodeLabel">Code</InputLabel>
                            <Select
                                labelId="countryCodeLabel"
                                id="country_code"
                                name="country_code"
                                value={state.country_code}
                                onChange={handleChange}
                                label="Code"
                                disabled
                            >
                                {allowedCountries.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>
                                        {country.code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 0 }} variant="outlined">
                            <InputLabel id="phoneLabel">Phone</InputLabel>
                            <OutlinedInput
                                id="phone"
                                label="Phone"
                                name="phone"
                                type="tel"
                                value={state.phone}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Box>
                    <FormControl
                        sx={{ flex: 1, minWidth: 0, mb: 2 }}
                        variant="outlined"
                    >
                        <InputLabel id="locationLabel">Location *</InputLabel>
                        <OutlinedInput
                            id="location"
                            label="Location *"
                            name="location"
                            value={state.location}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>
                </Box>
                <Box className="flex gap-3">
                    <FormControl
                        fullWidth={true}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        <InputLabel id="accountLabel">
                            Choose Account *
                        </InputLabel>
                        <Select
                            labelId="accountLabel"
                            id="account"
                            name="account"
                            value={state.account}
                            onChange={handleAccountChange}
                            label="Choose Account *"
                            required
                        >
                            {!isGetAccountLoading &&
                                account?.accounts?.map((acc) => (
                                    <MenuItem key={acc.uuid} value={acc.uuid}>
                                        {acc.company_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box className="flex gap-3">
                    <FormControl
                        fullWidth={true}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        <InputLabel id="endpointLabel">Endpoints *</InputLabel>
                        <Select
                            labelId="endpointLabel"
                            id="endpoint"
                            name="Endpoint"
                            value={state.Endpoint}
                            onChange={handleEndpointChange}
                            label="Endpoints *"
                            required
                        >
                            {user?.Endpoints?.map((endpoint) => (
                                <MenuItem key={endpoint.uuid} value={endpoint}>
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box className="flex gap-3">
                    <FormControl
                        fullWidth={true}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    >
                        <InputLabel id="currencyLabel">Currency *</InputLabel>
                        <Select
                            labelId="currencyLabel"
                            id="currency"
                            label="Currency *"
                            name="currency"
                            value={state.currency}
                            onChange={handleChange}
                            required
                        >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="CHF">CHF</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                {/* file input */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel
                        id="demo-multiple-name-label"
                        sx={{ background: '#F5F5F5', display: 'none' }}
                    >
                        Profile CSV
                    </InputLabel>
                    <Input
                        type="file"
                        id="profile"
                        name="fd"
                        onChange={handleChangeFile}
                    />
                </FormControl>
            </div>
            <div className="modal-footer">
                <Button
                    className="modal-button-secondary"
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    className="modal-button-primary"
                    disabled={
                        !state.name ||
                        !state.country ||
                        !state.location ||
                        !state.account ||
                        !state.currency ||
                        !state.Endpoint
                    }
                >
                    Submit
                </Button>
            </div>
        </Box>
    );
});

export default CreatePortfolio;

